import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm font-display font-bold uppercase tracking-[0.15em] text-sm ring-offset-background transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 shadow-hard hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] active:translate-x-[5px] active:translate-y-[5px]",
  {
    variants: {
      variant: {
        default: "bg-navy text-white border-2 border-navy hover:bg-navy-deep",
        destructive: "bg-destructive text-white border-2 border-navy hover:bg-destructive/90",
        outline: "bg-cream text-navy border-2 border-navy hover:bg-white",
        secondary: "bg-navy text-lime border-2 border-navy hover:bg-navy-deep",
        ghost: "shadow-none hover:shadow-none hover:translate-x-0 hover:translate-y-0 hover:bg-accent hover:text-accent-foreground border-0",
        link: "shadow-none hover:shadow-none hover:translate-x-0 hover:translate-y-0 text-navy underline-offset-4 hover:underline border-0",
        cta: "bg-lime text-navy border-2 border-navy hover:bg-lime/90",
        hero: "bg-transparent text-cream border-2 border-cream hover:bg-cream hover:text-navy",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 px-4 text-xs",
        lg: "h-12 px-7 text-sm",
        xl: "h-14 px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
